/* eslint-disable @typescript-eslint/no-non-null-assertion */

import {
  Component,
  ContentChildren,
  Directive,
  Input,
  ViewEncapsulation,
  QueryList,
  ChangeDetectionStrategy,
  AfterViewInit,
  HostListener,
  ViewChild,
  ElementRef,
} from "@angular/core";
import { theme } from "@vattenfall/design-system";
import {
  Debounce,
  BooleanInput,
  coerceBooleanProperty,
  coerceNumberProperty,
  getZScores,
  hexToRgb,
  NumberInput,
  rgbToHex,
} from "@vattenfall/util";

/** Color variations supported by heatmap */
const COLORS = <const>{
  green: theme.colors.secondary.green.DEFAULT,
  red: theme.colors.secondary.red.DEFAULT,
  blue: theme.colors.primary.blue.DEFAULT,
  yellow: theme.colors.primary.yellow.DEFAULT,
};

/** Type describing heatmap color variations */
type Color = keyof typeof COLORS;

/**
 * Heat map table cell directly nested under `<ui-heatmap-section>`
 */
@Directive({
  selector: "ui-heatmap-plot",
})
export class HeatmapPlotDirective {
  /** Label of column. Required. */
  @Input() label!: string;
  /** Data value of heatmap cell. Required. */
  @Input()
  get data(): number | null {
    return this._data;
  }
  set data(value: NumberInput) {
    this._data = coerceNumberProperty(value);
  }
  _data: number | null = null;
  /** Whether the table cell should be wrapped in a yellow outline */
  @Input()
  get outline(): boolean {
    return this._outline;
  }
  set outline(value: BooleanInput) {
    this._outline = coerceBooleanProperty(value);
  }
  private _outline = false;

  color = "";
}

/**
 * Heat map table row directly nested under `<ui-heatmap>`
 */
@Directive({
  selector: "ui-heatmap-section",
})
export class HeatmapSectionDirective {
  /** Label of row. Required. */
  @Input() label!: string;
  @ContentChildren(HeatmapPlotDirective) readonly heatmapPlotDirectives!: QueryList<HeatmapPlotDirective>;
}

/**
 * Vattenfall UI `ui-heatmap` is a graphical representation of data that uses a system of color-coding to represent different values.
 *
 * Data is drawn in two ordinal dimensions by declaring `<ui-heatmap-section>` elements on the _x_ axis and nested `<ui-heatmap-plot>` elements on the `y` axis. A `unit` value can be provided for specifying the type of data that is being presented.
 *
 * A restricted range of brand colors can be used for representing low and and high values.
 */
@Component({
  selector: "ui-heatmap",
  templateUrl: "./heatmap.component.html",
  styleUrls: ["./heatmap.component.scss"],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeatmapComponent implements AfterViewInit {
  /** Unit of heatmap data */
  @Input() unit = "";
  /** Color of lowest value */
  @Input() lowColor: Color = "blue";
  /** Color of highest value */
  @Input() highColor: Color = "red";

  @ViewChild("tableElement") private tableElement!: ElementRef<HTMLTableElement>;
  @ContentChildren(HeatmapPlotDirective, { descendants: true }) readonly heatmapPlotDirectives!: QueryList<HeatmapPlotDirective>;
  @ContentChildren(HeatmapSectionDirective) readonly heatmapSectionDirectives!: QueryList<HeatmapSectionDirective>;

  // Value of table cell on hover
  activeValue: number | null = null;

  /** Highest value in provided range of data */
  get highValue() {
    return Math.max(...this.heatmapPlotDirectives.map((heatmapPlotDirective) => heatmapPlotDirective.data!).filter(Number));
  }

  /** Lowest value in provided range of data */
  get lowValue() {
    return Math.min(...this.heatmapPlotDirectives.map((heatmapPlotDirective) => heatmapPlotDirective.data!).filter(Number));
  }

  /** Gradient from lowest to highest value in range of data */
  get gradient() {
    return `linear-gradient(90deg, ${COLORS[this.lowColor]}, ${COLORS[this.highColor]})`;
  }

  /** Minimum width of row labels */
  get width() {
    return (
      this.heatmapSectionDirectives.reduce(
        (chars, heatmapSectionDirective) =>
          chars > heatmapSectionDirective.label.length ? chars : heatmapSectionDirective.label.length,
        0
      ) + "ch"
    );
  }

  /** Column labels */
  get labels() {
    return this.heatmapSectionDirectives.get(0)?.heatmapPlotDirectives.map((heatmapPlotDirective) => heatmapPlotDirective.label);
  }

  @HostListener("window:resize")
  @Debounce(50)
  onResize() {
    this.setLabels();
  }

  ngAfterViewInit() {
    this.setColors();
    this.setLabels();
  }

  /**
   * Adjusts the number of labels displayed to match the available screen real estate
   */
  setLabels() {
    // Minimum spacing between labels
    const MIN_LABEL_SPACING = 14;

    const labelElements = this.tableElement.nativeElement.querySelectorAll<HTMLTableHeaderCellElement>("th[data-label]");

    // Reset height and width to get the correct plot size
    labelElements.forEach((labelElement) => {
      labelElement.textContent = labelElement.dataset.label!;
      if (labelElement.scope === "row") labelElement.style.setProperty("height", "");
    });

    const plotSize = this.tableElement.nativeElement.querySelector<HTMLTableDataCellElement>("td")!.getBoundingClientRect().width;

    labelElements.forEach((labelElement, index) => {
      // Labels are to condensed
      if (plotSize < MIN_LABEL_SPACING) {
        // Remove text for every odd label
        if (index % 2 === 0) labelElement.textContent = "";
        else labelElement.textContent = labelElement.dataset.label!;
        // Ensure that there is no whitespace between rows when plot size is less than label line-height
        if (labelElement.scope === "row") labelElement.style.setProperty("height", plotSize + "px");
      }
    });
  }

  /**
   * Sets heatmap plot background color based on its provided data property
   */
  setColors() {
    const zScores = getZScores(this.heatmapPlotDirectives.map((heatmapPlotDirective) => heatmapPlotDirective.data));
    // Calculate the range of z-scores
    const minScore = Math.min(...zScores.filter(Number));
    const maxScore = Math.max(...zScores.filter(Number));
    // Map the z-scores to an array of color codes
    zScores.forEach((zScore, index) => {
      // Use white background color for non-number values
      if (Number.isNaN(zScore)) this.heatmapPlotDirectives.get(index)!.color = "#FFFFFF";
      // Calculate the color of the cell based on the z-score
      const colorRatio = (zScore - minScore) / (maxScore - minScore);
      // Calculated color represented as hex value
      const color = rgbToHex(
        hexToRgb(COLORS[this.lowColor]).map((value, index) =>
          Math.round(value + colorRatio * (hexToRgb(COLORS[this.highColor])[index] - value))
        )
      );
      this.heatmapPlotDirectives.get(index)!.color = color;
    });
  }
}
